import { Link, NavLink, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useDashboard } from "@/lib/dataContext";
import { useAuth } from "@/auth/AuthProvider";
import { canViewOverview } from "@/auth/authorization";
import {
    LayoutDashboard,
    TrendingUp,
    Users,
    CalendarRange,
    Sprout,
    ShieldCheck,
    HeartHandshake,
    Map,
    Building2,
    X,
} from "lucide-react";

export const NAV_ITEMS = [
    { to: "/", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/trends", label: "Monthly Trends", icon: TrendingUp },
    { to: "/membership", label: "Membership", icon: Users },
    { to: "/activities", label: "Activities & Wings", icon: CalendarRange },
    { to: "/youth", label: "Youth Engagement", icon: Sprout },
    { to: "/leadership", label: "Leadership", icon: ShieldCheck },
    { to: "/outreach", label: "Service & Outreach", icon: HeartHandshake },
    { to: "/districts", label: "Districts", icon: Map },
    { to: "/samithis", label: "Samithis", icon: Building2 },
];

/* const SaiEmblem = () => (
    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-warm text-sidebar-accent-foreground shadow-soft">
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.6">
            <path d="M12 2l1.6 3.6L17.5 4l-1 4 4-1-2.6 3.4L21 12l-3.1 1.6L20.5 17l-4-1 1 4-3.4-2.6L12 21l-1.6-3.6L7 20l1-4-4 1 2.6-3.4L3 12l3.6-1.6L4 7l4 1-1-4 3.4 2.6L12 2z" strokeLinejoin="round" />
            <circle cx="12" cy="12" r="3" fill="currentColor" stroke="none" opacity="0.9" />
        </svg>
    </span>
); */

const SaiEmblem = () => (
    <img
        src="/images/sssso-logo.png"
        alt="Sri Sathya Sai Seva Organisations"
        className="h-14 w-14 shrink-0 object-contain"
    />
);

export const SidebarContent = ({ onNavigate }) => {
    const location = useLocation();
    const search = location.search;
    const { data, stateMeta } = useDashboard();
    const { user } = useAuth();
    const stateName = data?.state_profile?.state_name || data?.executive_summary?.state_name || stateMeta?.state_name || "State";
    const period = data?.reporting_period?.period_label || "FY 2025–26";
    return (
        <div className="flex h-full flex-col bg-sidebar text-sidebar-foreground">
           	
			<div className="flex items-center gap-4 px-5 py-6">
				<SaiEmblem />

					<div className="min-w-0">
						<p className="font-serif-elegant text-[17px] leading-tight text-sidebar-foreground">
							Sri Sathya Sai
						</p>

						<p className="mt-1 text-[13px] leading-tight text-sidebar-muted">
							Seva Organisations
						</p>
					</div>
			</div>

            <div className="mx-5 mb-4 rounded-xl border border-sidebar-border/70 bg-sidebar-hover/60 px-4 py-3">
                <p className="text-[11px] uppercase tracking-wider text-sidebar-muted">State</p>
                <p className="font-serif-elegant text-lg text-sidebar-foreground">{String(stateName).trim()}</p>
                <p className="text-[11px] text-sidebar-muted">{period}</p>
            </div>

            <nav className="flex-1 space-y-1 overflow-y-auto px-3 pb-6">
                {NAV_ITEMS.map((item) => (
                    <NavLink
                        key={item.to}
                        to={{ pathname: item.to, search }}
                        end={item.end}
                        onClick={onNavigate}
                        className={({ isActive }) =>
                            cn(
                                "group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-200",
                                isActive
                                    ? "bg-sidebar-accent/15 text-sidebar-accent"
                                    : "text-sidebar-foreground/80 hover:bg-sidebar-hover hover:text-sidebar-foreground",
                            )
                        }
                    >
                        {({ isActive }) => (
                            <>
                                <span
                                    className={cn(
                                        "flex h-8 w-8 items-center justify-center rounded-md transition-colors",
                                        isActive
                                            ? "bg-sidebar-accent/20 text-sidebar-accent"
                                            : "text-sidebar-muted group-hover:text-sidebar-foreground",
                                    )}
                                >
                                    <item.icon className="h-[18px] w-[18px]" strokeWidth={2} />
                                </span>
                                <span className="truncate">{item.label}</span>
                            </>
                        )}
                    </NavLink>
                ))}
            </nav>

            <div className="border-t border-sidebar-border px-5 py-4">
                {canViewOverview(user) && (
                    <Link to="/" onClick={onNavigate} className="mb-3 flex items-center gap-2 text-xs font-medium text-sidebar-accent hover:underline">
                        ← {user.scopeType === "ZONE" ? "Zonal Insights" : "National Dashboard"}
                    </Link>
                )}
                <p className="text-[11px] leading-relaxed text-sidebar-muted">
                    Love All &middot; Serve All
                </p>
            </div>
        </div>
    );
};

export const Sidebar = () => (
    <aside className="fixed inset-y-0 left-0 z-30 hidden w-[264px] border-r border-sidebar-border lg:block">
        <SidebarContent />
    </aside>
);

export const MobileSidebarClose = ({ onClose }) => (
    <button
        onClick={onClose}
        className="absolute right-3 top-4 z-10 rounded-md p-1.5 text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-foreground"
        aria-label="Close menu"
    >
        <X className="h-5 w-5" />
    </button>
);
